import { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaCheck, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import { FaIcon, FA_ICON_MAP } from '@/utils/faIconMap';
import { fetchTemplates, fetchTemplate, createBoardFromTemplate } from '@/services/kanbanBoardService';
import '@/styles/ActionItemsStyles.scss';
import * as Sentry from '@sentry/react';

/**
 * CreateBoardModal Component
 * Enhanced modal for creating a new Kanban board with template selection
 * @param {Object} props Component props
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onCreate - Callback when board is created (for custom board)
 */
const CreateBoardModal = ({ onClose, onCreate }) => {
    const { apiFetch } = useAuth();
    const [creationMode, setCreationMode] = useState('template'); // 'template' or 'custom'
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateDetails, setTemplateDetails] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    
    // Custom board fields
    const [boardName, setBoardName] = useState('');
    const [boardDescription, setBoardDescription] = useState('');
    const [boardIcon, setBoardIcon] = useState('fa-clipboard');
    const [boardColor, setBoardColor] = useState('#3498db');
    const [creating, setCreating] = useState(false);
    const [showIconPicker, setShowIconPicker] = useState(false);
    const [iconSearchQuery, setIconSearchQuery] = useState('');

    // All available icons from the FA_ICON_MAP for the icon picker
    const availableIcons = Object.keys(FA_ICON_MAP);
    const filteredIcons = iconSearchQuery
        ? availableIcons.filter(iconKey => iconKey.toLowerCase().includes(iconSearchQuery.toLowerCase()))
        : availableIcons;

    const loadTemplates = useCallback(async () => {
        setLoadingTemplates(true);
        try {
            const data = await fetchTemplates(apiFetch);
            setTemplates(data);
            if (data.length > 0) {
                setSelectedTemplate(data[0].templateId);
            }
        } catch (catchError) {
            Sentry.captureException(catchError);
            toast.error('Failed to load templates');
        } finally {
            setLoadingTemplates(false);
        }
    }, [apiFetch]);

    const loadTemplateDetails = useCallback(async (templateId) => {
        try {
            const details = await fetchTemplate(apiFetch, templateId);
            setTemplateDetails(details);
            // Pre-fill fields with template data
            setBoardName(details.name);
            setBoardDescription(details.description || '');
            setBoardIcon(details.icon || '📋');
            setBoardColor(details.color || '#3498db');
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to load template details:', error);
        }
    }, [apiFetch]);
    
    // Load templates on mount — async IIFE pattern satisfies react-hooks/set-state-in-effect
    useEffect(() => {
        (async () => {
            if (creationMode === 'template') {
                await loadTemplates();
            }
        })();
    }, [creationMode, loadTemplates]);
    
    // Load template details when one is selected
    useEffect(() => {
        (async () => {
            if (selectedTemplate) {
                await loadTemplateDetails(selectedTemplate);
            }
        })();
    }, [selectedTemplate, loadTemplateDetails]);
    
    
    
    const handleCreate = async () => {
        if (!boardName.trim()) {
            toast.error('Board name is required');
            return;
        }

        setCreating(true);
        try {
            if (creationMode === 'template' && selectedTemplate) {
                // Create from template
                const _result = await createBoardFromTemplate(apiFetch, selectedTemplate, {
                    name: boardName,
                    description: boardDescription,
                    isPublic: false
                });
                toast.success('Board created from template successfully');
                onClose();
                // Refresh the parent component
                window.location.reload();
            } else {
                // Create custom board
                await onCreate({
                    name: boardName,
                    description: boardDescription,
                    icon: boardIcon,
                    color: boardColor,
                    isPublic: false,
                    createDefaultColumns: true
                });
                toast.success('Board created successfully');
                onClose();
            }
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to create board');
        } finally {
            setCreating(false);
        }
    };
    
    const renderTemplateColumns = () => {
        if (!templateDetails || !templateDetails.columnsJson) {
            return null;
        }
        
        try {
            const columns = JSON.parse(templateDetails.columnsJson);
            return (
                <div className="template-columns-preview">
                    <label><FaInfoCircle /> Columns that will be created:</label>
                    <div className="columns-list">
                        {columns.map((col, idx) => (
                            <div key={idx} className="column-preview-item" style={{ borderLeft: `4px solid ${col.color || '#999'}` }}>
                                <span className="column-icon">{col.icon || '📌'}</span>
                                <span className="column-name">{col.name}</span>
                                {col.wipLimit && <span className="wip-badge">WIP: {col.wipLimit}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            );
        } catch (catchError) {
            Sentry.captureException(catchError);
            return null;
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Create New Board</h2>
                    <button className="close-button" onClick={onClose}>
                        <FaTimes />
                    </button>
                </div>

                <div className="modal-body">
                    {/* Creation Mode Toggle */}
                    <div className="toggle-group">
                        <button 
                            className={`toggle-button ${creationMode === 'template' ? 'active' : ''}`}
                            onClick={() => setCreationMode('template')}
                        >
                            Use Template
                        </button>
                        <button 
                            className={`toggle-button ${creationMode === 'custom' ? 'active' : ''}`}
                            onClick={() => setCreationMode('custom')}
                        >
                            Custom Board
                        </button>
                    </div>
                    
                    {/* Template Selection */}
                    {creationMode === 'template' && (
                        <>
                            {loadingTemplates ? (
                                <div className="loading-state">Loading templates...</div>
                            ) : templates.length === 0 ? (
                                <div className="empty-state">No templates available</div>
                            ) : (
                                <div className="form-group">
                                    <label htmlFor="templateSelect">Select Template</label>
                                    <select
                                        id="templateSelect"
                                        value={selectedTemplate || ''}
                                        onChange={(e) => setSelectedTemplate(parseInt(e.target.value, 10))}
                                        className="board-template-select"
                                    >
                                        {templates.map(template => (
                                            <option key={template.templateId} value={template.templateId}>
                                                {template.icon} {template.name}
                                                {template.isSystem && ' (System)'}
                                                {template.category && ` - ${template.category}`}
                                            </option>
                                        ))}
                                    </select>
                                    {templateDetails && (
                                        <div className="template-description">
                                            <FaInfoCircle /> {templateDetails.description}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            {templateDetails && renderTemplateColumns()}
                        </>
                    )}
                    
                    {/* Board Details */}
                    <div className="form-group">
                        <label htmlFor="boardName">Board Name *</label>
                        <input
                            id="boardName"
                            type="text"
                            value={boardName}
                            onChange={(e) => setBoardName(e.target.value)}
                            placeholder="e.g., Q1 Marketing Tasks, IT Support Queue"
                            autoFocus
                            maxLength={200}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="boardDescription">Description</label>
                        <textarea
                            id="boardDescription"
                            value={boardDescription}
                            onChange={(e) => setBoardDescription(e.target.value)}
                            placeholder="Optional description of this board's purpose"
                            rows={3}
                            maxLength={1000}
                        />
                    </div>

                    {creationMode === 'custom' && (
                        <>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="boardIcon">Icon</label>
                                    <div className="icon-picker-trigger">
                                        <button
                                            type="button"
                                            className="icon-picker-button"
                                            onClick={() => setShowIconPicker(!showIconPicker)}
                                        >
                                            <FaIcon icon={boardIcon} size={20} />
                                            <span className="icon-name">{boardIcon}</span>
                                        </button>
                                        {showIconPicker && (
                                            <div className="icon-picker-dropdown">
                                                <div className="icon-picker-search">
                                                    <input
                                                        type="text"
                                                        value={iconSearchQuery}
                                                        onChange={(e) => setIconSearchQuery(e.target.value)}
                                                        placeholder="Search icons..."
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="icon-picker-grid">
                                                    {filteredIcons.slice(0, 60).map(iconKey => (
                                                        <button
                                                            key={iconKey}
                                                            type="button"
                                                            className={`icon-picker-item ${boardIcon === iconKey ? 'selected' : ''}`}
                                                            onClick={() => {
                                                                setBoardIcon(iconKey);
                                                                setShowIconPicker(false);
                                                                setIconSearchQuery('');
                                                            }}
                                                            title={iconKey}
                                                        >
                                                            <FaIcon icon={iconKey} size={18} />
                                                        </button>
                                                    ))}
                                                </div>
                                                {filteredIcons.length > 60 && (
                                                    <div className="icon-picker-hint">
                                                        Showing 60 of {filteredIcons.length} — refine your search
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="boardColor">Color</label>
                                    <input
                                        id="boardColor"
                                        type="color"
                                        value={boardColor}
                                        onChange={(e) => setBoardColor(e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="info-box">
                                <FaInfoCircle />
                                <p>A board will be created with default columns: To Do, In Progress, Done, and Closed. You can customize these later in board settings.</p>
                            </div>
                        </>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose} disabled={creating}>
                        Cancel
                    </button>
                    <button className="btn-primary" onClick={handleCreate} disabled={creating || (creationMode === 'template' && !selectedTemplate)}>
                        {creating ? 'Creating...' : 'Create Board'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateBoardModal;
